import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { marketAPI, tradingAPI } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import { TrendingUp, TrendingDown, Wallet, BarChart3, ArrowUpRight, ArrowDownRight, Wifi, WifiOff, Activity } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

const formatCurrency = (v) => v === undefined || v === null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const { prices: wsPrices, connected: wsConnected } = useWebSocket();
  const [coins, setCoins] = useState([]);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [coinsRes, posRes, tradesRes] = await Promise.all([
          marketAPI.getTopCoins(10), tradingAPI.getPositions(), tradingAPI.getHistory(10)
        ]);
        setCoins(coinsRes.data); setPositions(posRes.data); setTrades(tradesRes.data);
        refreshUser();
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 2000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Merge WebSocket prices with initial data
  const displayCoins = wsPrices.length > 0 ? wsPrices : coins;

  const totalPnl = positions.reduce((s, p) => s + (p.profit_loss || 0), 0);
  const totalInvested = positions.reduce((s, p) => s + (p.trade_amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="stat-balance">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Wallet Balance</span>
              <Wallet className="w-4 h-4 text-[var(--text-secondary)]" />
            </div>
            <p className="text-2xl font-bold font-mono text-[var(--text-primary)]">{formatCurrency(user?.wallet_balance)}</p>
          </div>
          {/* <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="stat-leverage">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Leverage Fund</span>
              <TrendingUp className="w-4 h-4 text-blue-400" />
            </div>
            <p className="text-2xl font-bold font-mono text-blue-400">{formatCurrency(user?.leverage_fund || 0)}</p>
          </div> */}
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="stat-total-trading">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Trading Balance</span>
              <BarChart3 className="w-4 h-4 text-[var(--text-secondary)]" />
            </div>
            <p className="text-2xl font-bold font-mono text-emerald-400">{formatCurrency(user?.total_trading_balance || (user?.wallet_balance || 0) + (user?.leverage_fund || 0))}</p>
          </div>
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="stat-pnl">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Open P/L</span>
              {(user?.total_pnl || totalPnl) >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
            </div>
            <p className={`text-2xl font-bold font-mono ${(user?.total_pnl || totalPnl) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {(user?.total_pnl || totalPnl) >= 0 ? '+' : ''}{formatCurrency(user?.total_pnl || totalPnl)}
            </p>
          </div>
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="stat-positions">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Open Positions</span>
              <Activity className="w-4 h-4 text-[var(--text-secondary)]" />
            </div>
            <p className="text-2xl font-bold font-mono text-[var(--text-primary)]">{user?.open_positions_count || positions.length}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Open Positions */}
          <div className="lg:col-span-8">
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="open-positions-card">
              <div className="px-5 py-4 border-b border-[var(--border-subtle)]">
                <h2 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)]">Open Positions</h2>
              </div>
              <div className="overflow-x-auto">
                {positions.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No open positions. Start trading!</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                        <th className="text-left px-5 py-3">Pair</th>
                        <th className="text-left px-3 py-3">Type</th>
                        <th className="text-right px-3 py-3">Volume</th>
                        {/* <th className="text-right px-3 py-3">Amount</th> */}
                        <th className="text-right px-3 py-3">Entry</th>
                        {/* <th className="text-right px-3 py-3">Current</th> */}
                        <th className="text-right px-5 py-3">P/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((p) => (
                        <tr key={p.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                          <td className="px-5 py-3 font-mono text-[var(--text-primary)]">{p.symbol?.toUpperCase()}</td>
                          <td className="px-3 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                              {p.type?.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{p.volume}</td>
                          {/* <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(p.trade_amount)}</td> */}
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(p.entry_price)}</td>
                          {/* <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(p.current_price)}</td> */}
                          <td className={`px-5 py-3 text-right font-mono font-medium ${(p.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {(p.profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(p.profit_loss)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Recent Trades */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded mt-6" data-testid="recent-trades-card">
              <div className="px-5 py-4 border-b border-[var(--border-subtle)]">
                <h2 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)]">Recent Trades</h2>
              </div>
              <div className="overflow-x-auto">
                {trades.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No trades yet</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                        <th className="text-left px-5 py-3">Pair</th>
                        <th className="text-left px-3 py-3">Type</th>
                        <th className="text-left px-3 py-3">Status</th>
                        <th className="text-right px-3 py-3">Entry</th>
                        <th className="text-right px-5 py-3">P/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trades.slice(0, 5).map((t) => (
                        <tr key={t.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                          <td className="px-5 py-3 font-mono text-[var(--text-primary)]">{(t.symbol || t.coin_symbol)?.toUpperCase()}</td>
                          <td className="px-3 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                              {t.type?.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`text-xs ${t.status === 'open' ? 'text-green-400' : 'text-[var(--text-secondary)]'}`}>{t.status}</span>
                          </td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(t.entry_price)}</td>
                          <td className={`px-5 py-3 text-right font-mono font-medium ${(t.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {t.status === 'open' ? '-' : `${(t.profit_loss || 0) >= 0 ? '+' : ''}${formatCurrency(t.profit_loss)}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Market Overview */}
          <div className="lg:col-span-4">
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="market-overview-card">
              <div className="px-5 py-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
                <h2 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)]">Market Overview</h2>
                <div className="flex items-center gap-1.5" data-testid="ws-status">
                  {wsConnected ? <Wifi className="w-3 h-3 text-emerald-400" /> : <WifiOff className="w-3 h-3 text-[var(--text-tertiary)]" />}
                  <span className={`text-[10px] font-medium ${wsConnected ? 'text-emerald-400' : 'text-[var(--text-tertiary)]'}`}>
                    {wsConnected ? 'LIVE' : 'OFFLINE'}
                  </span>
                </div>
              </div>
              <div className="divide-y divide-[var(--divide-color)]">
                {loading && displayCoins.length === 0 ? (
                  <div className="p-6 text-center text-sm text-[var(--text-secondary)]">Loading...</div>
                ) : (
                  displayCoins.slice(0, 8).map((c) => (
                    <div key={c.id} className="px-5 py-3 flex items-center justify-between hover:bg-[var(--hover-bg)] transition-colors">
                      <div className="flex items-center gap-3">
                        <img src={c.image} alt={c.symbol} className="w-7 h-7 rounded-full" />
                        <div>
                          <p className="text-sm font-medium text-[var(--text-primary)]">{c.symbol?.toUpperCase()}</p>
                          <p className="text-xs text-[var(--text-secondary)]">{c.name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono text-[var(--text-primary)]">{formatCurrency(c.current_price)}</p>
                        <div className={`flex items-center justify-end gap-1 text-xs font-mono ${(c.price_change_24h || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(c.price_change_24h || 0) >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {Math.abs(c.price_change_24h || 0).toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Portfolio Chart */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded mt-6 p-5" data-testid="portfolio-chart-card">
              <h2 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] mb-4">Portfolio Performance</h2>
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={Array.from({ length: 30 }, (_, i) => ({ d: i, v: (user?.wallet_balance || 10000) + Math.sin(i * 0.3) * 500 + i * 20 }))}>
                    <defs>
                      <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="v" stroke="#3B82F6" strokeWidth={2} fill="url(#portfolioGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
